import type { ClinicalInsuranceCategory } from '@gh/common/models'

export type BookingTrackingEventKind =
  | 'step'
  | 'click'
  | 'page_load'
  | 'checkout_rendered'
  | 'checkout_completed'

export type BookingTrackingCheckoutVariant =
  | 'self_pay'
  | 'insured_copay'
  | 'insured_full_price'
  | 'settlement'

export type BookingTrackingNavigationType = 'navigate' | 'reload' | 'back_forward' | 'prerender'

/** All fields optional so persisted / legacy documents remain valid. */
export type BookingTrackingEvent = {
  /** Browser tab session that recorded the event (Firestore path is authoritative). */
  sessionId?: string
  /** Buffer-only: consultation the event was recorded for (per-tab localStorage). */
  consultationId?: string
  kind?: BookingTrackingEventKind
  path?: string
  /** Current booking route step when the event was recorded. */
  step?: string
  /** Previous step when `kind` is `step`. */
  fromStep?: string
  /** Stable control id (`data-gh-id`) when `kind` is `click`. */
  buttonId?: string
  /** Visible button or link label when `kind` is `click`. */
  buttonText?: string
  /** @deprecated Prefer `buttonId`. Legacy events may still use `target`. */
  target?: string
  category?: ClinicalInsuranceCategory
  payerId?: string
  checked?: boolean
  checkout?: BookingTrackingCheckoutVariant
  slotDate?: string
  slotTime?: string
  navigation?: BookingTrackingNavigationType
  /** Consultations hub cancel: within late-cancellation window. */
  insideWindow?: boolean
  /** Consultations hub cancel: late fee may apply. */
  feeOwed?: boolean
  /** Consultations hub cancel API result when relevant. */
  cancelOutcome?: 'success' | 'error'
  /**
   * Buffer timestamp key persisted on Firestore array events so arrayUnion can distinguish
   * real duplicates and admin timelines keep stable sort keys.
   */
  clientEventKey?: string
}

export type BookingTrackingChangesMap = Record<string, BookingTrackingEvent>

export const BOOKING_TRACKING_LOCAL_STORAGE_KEY = 'gh.bookingTracking.v1'

/** Minimum interval between automatic Firestore merges while the booking flow is open. */
export const BOOKING_TRACKING_FLUSH_THROTTLE_MS = 10_000

/** @deprecated Use BOOKING_TRACKING_FLUSH_THROTTLE_MS */
export const BOOKING_TRACKING_DEBOUNCE_MS = BOOKING_TRACKING_FLUSH_THROTTLE_MS

export type BookingTrackingPersistedBuffer = {
  events: BookingTrackingChangesMap
}
